import {
  Box,
  Card,
  FormControl,
  FormLabel,
  Link,
  Switch,
  Text,
  Stack,
  Icon,
  Heading
} from '@homebotapp/hb-react-component-catalog'
import { useState } from 'react'
import { isAuthTokenSupported } from '../../../auth'
import { useToggleFeedSubscribed } from '../../../hooks/gallery/useToggleFeedSubscribed'
import { GallerySubscribeModal } from '../../../Components/homeSearch/GallerySubscribeModal/GallerySubscribeModal'
import { FeedQuery, FeedBase, FeedType } from '../../../api/gqlaxy/gql/generated/graphql'
import { defineMessages, useIntl } from 'react-intl'
import { SEARCH_PATH_HELPERS } from '../../../constants/navigation'
import { useParams } from 'react-router-dom'
import { Link as ReactRouterLink } from 'react-router-dom'
import { AssumablesLearnMoreModal } from '../../../Components/homeSearch/AssumablesLearnMoreModal/AssumablesLearnMoreModal'
import { useGetClientId } from '../../../hooks/useGetClientId'
import { BuyerPerksLearnMoreModal } from '../../../Components/homeSearch/BuyerPerksLearnMoreModal/BuyerPerksLearnMoreModal'
import useViewport from '../../../hooks/useViewport'

const MSG = defineMessages({
  viewAll: {
    id: 'General.viewAll',
    defaultMessage: 'View all'
  },
  learnMore: {
    id: 'General.learnMore',
    defaultMessage: 'Learn more'
  },
  alerts: {
    id: 'Gallery.alerts',
    defaultMessage: 'Alerts'
  },
  on: {
    id: 'Gallery.on',
    defaultMessage: 'on'
  },
  off: {
    id: 'Gallery.off',
    defaultMessage: 'off'
  }
})
interface GalleryTypeWithAlertToggleProps {
  updateFrequency: string
  title: string
  refetch: () => void
  feed: FeedBase
  data: FeedQuery
  children?: React.ReactNode
}

export const GalleryTypeWithAlertToggle = ({
  updateFrequency,
  title,
  refetch,
  feed,
  data,
  children
}: GalleryTypeWithAlertToggleProps) => {
  const intl = useIntl()
  const viewport = useViewport()
  const { mutateAsync: toggleSub } = useToggleFeedSubscribed()
  const [showSubscribedModal, setShowSubscribedModal] = useState(false)
  const params = useParams<{ customerProfileId: string }>()
  const clientId = useGetClientId()

  const toggleSubscription = async () => {
    if (!isAuthTokenSupported()) {
      return
    }
    await toggleSub({ feedId: feed.id })
    await refetch()
    if (data?.feed.subscribed) {
      return
    }
    setShowSubscribedModal(true)
  }

  return (
    <Card
      p={0}
      borderRadius={16}
      overflow={'hidden'}
      w={viewport.isSmall ? '90vw' : '200px'}
      display='flex'
      flexDirection='column'
      justifyContent='space-between'
    >
      <Stack
        direction='column'
        align='stretch'
        px={4}
        pb={viewport.isSmall ? '16px' : 3}
        paddingTop={viewport.isSmall ? '8px' : 12}
      >
        <Heading
          variant='eyebrow'
          size='2xs'
          color='neutral.500'
          textAlign='left'
          fontWeight='semibold'
          textTransform='uppercase'
          mb={viewport.isSmall ? 0 : 1}
        >
          {updateFrequency}
        </Heading>
        <Text fontWeight='bold' color='neutral.900' textAlign='left' mb={0}>
          {title}
        </Text>
        <LearnMoreButtonAndModals feedType={data?.feed?.feedType} />
      </Stack>

      {viewport.isSmall && (
        <Box px={4} py={3} flexGrow={1} overflowY='hidden'>
          {children}
        </Box>
      )}

      <Box>
        <Link
          as={ReactRouterLink}
          to={SEARCH_PATH_HELPERS.gallery.buildPath(params.customerProfileId, feed.id, feed?.feedType, clientId)}
          color='primary.500'
          display='flex'
          alignItems='center'
          justifyContent='center'
          mb={3}
          tracking={{
            ui_context: 'Gallery_Feed',
            descriptor: `Clicked on Feed ${feed?.feedType}`,
            guid: `3GtcZMbe9HLD7YUKKiAyjg_${feed?.feedType}`
          }}
        >
          <Text mb={0} fontWeight='semibold'>
            {intl.formatMessage(MSG.viewAll)}
          </Text>
          <Icon name='circle-arrow-right-bts' ml={1} />
        </Link>
        <Box bg='card-bg-layer-3' px={4} py={3}>
          <FormControl gap='12px' width='100%' display='flex' alignItems='center' justifyContent='center'>
            <FormLabel htmlFor='alert-toggle' m={0} flexShrink={0}>
              {intl.formatMessage(MSG.alerts)}{' '}
              {data.feed.subscribed ? intl.formatMessage(MSG.on) : intl.formatMessage(MSG.off)}
            </FormLabel>
            <Switch
              m={0}
              id='alert-toggle'
              name='alert-toggle'
              isChecked={data.feed.subscribed}
              onChange={toggleSubscription}
              tracking={{
                guid: '6NU8QA8FBwaqxj40PgLWEN',
                ui_context: `For You Feeds Page - ${data.feed.feedType} gallery`,
                descriptor: data.feed.subscribed
                  ? `Subscribe to ${data.feed.feedType} feed`
                  : `Unsubscribe from ${data.feed.feedType} feed`
              }}
            />
          </FormControl>
        </Box>
      </Box>
      {showSubscribedModal && (
        <GallerySubscribeModal
          feedCategory={data?.feed.feedType as FeedType}
          onClose={() => {
            setShowSubscribedModal(false)
          }}
        />
      )}
    </Card>
  )
}

const LearnMoreButtonAndModals = ({ feedType }: { feedType: FeedType }) => {
  if (!feedType || (feedType !== FeedType.Assumables && feedType !== FeedType.Concessions)) {
    return null
  }
  const learnMoreAssumableButtonProps = {
    variant: 'link',
    colorScheme: 'primary',
    size: 'xs',
    tracking: {
      guid: '2qHGXjzHKCCCPDKnBtaA7J',
      ui_context: 'For You Gallery Feed',
      descriptor: 'Info button to learn more about assumables'
    }
  }

  return (
    <>
      {feedType === FeedType.Assumables && <AssumablesLearnMoreModal buttonProps={learnMoreAssumableButtonProps} />}
      {feedType === FeedType.Concessions && <BuyerPerksLearnMoreModal asLink />}
    </>
  )
}
